import { CardActions } from './ActionHosts';
import type { ActionDescriptor } from './ActionHosts';
import { StatusChip } from './StatusChip';

// AgentPlanCard — the agent workspace's proposed-plan/approval card (P10
// §2.4, story 2-144, this story's own primitive). EXTRACTED from
// `AgentPanel.tsx`'s hand-rolled approval block rather than designed fresh —
// per this story's own instruction, "the existing approval markup IS the
// specification." Only the RENDERING moved here: the decide/resolveApproval
// network call, `honestApprovalOutcome`'s copy resolution and every other
// piece of gateway/approval-rule logic stays in `AgentPanel.tsx` (explicitly
// out of this story's scope) and is handed in as plain props/callbacks — this
// component makes no domain imports and no gateway calls of its own, per P10
// §2.4's own contract for it.
//
// Two shapes, same card (both pre-existing, from `AgentPanel.tsx`):
//   - a BATCHED plan (story 1D-2, `governance_plan`-style): `steps` is
//     present, rendered as an ordered list with an optional `summary` line.
//   - a single-tool approval: `steps` is absent, `args` renders flat.
// Both share one pending/resolved footer: `pending` renders Approve/Decline
// (real `Button`s, not a raw `btn-glass` class); anything else renders the
// caller-resolved `outcome` — a `StatusChip` (WCAG 1.4.1: icon + label, never
// colour alone) alongside the honest headline sentence, with the raw error
// (if any) demoted to a collapsible `<details>` "Technical detail", exactly
// as the original inline markup did.
export interface AgentPlanCardStep {
  tool: string;
  args: Record<string, unknown>;
}

export interface AgentPlanCardOutcome {
  headline: string;
  detail?: string | undefined;
  isFailure: boolean;
}

export interface AgentPlanCardProps {
  /** The approval's own tool (a batched plan's wrapper tool, e.g. `governance_plan`, or the single tool being approved). */
  tool: string;
  /** Top-level args — rendered flat for a single-tool approval; read for a `summary` string when `steps` is a batched plan. */
  args: Record<string, unknown>;
  /** Present for a batched (multi-step) plan (story 1D-2); absent for a single-tool approval. */
  steps?: AgentPlanCardStep[] | undefined;
  /** `pending` renders Approve/Decline; any other value renders `outcome`. */
  status: 'pending' | 'approved' | 'declined' | 'expired' | 'failed';
  /** Caller-resolved outcome copy/tone (`AgentPanel`'s own `honestApprovalOutcome`) — required once `status !== 'pending'`. */
  outcome?: AgentPlanCardOutcome | undefined;
  onApprove: () => void;
  onDecline: () => void;
  className?: string;
  'data-testid'?: string;
}

// Story 2-26(a): a batched plan's step args can carry a "$<step>.<path>"
// reference into an earlier step's result (1D-5) — mechanically correct on
// the wire, but showing that raw token to a human reads as an internal
// implementation detail. Render a short natural-language phrase instead; the
// raw reference stays available as a tooltip (title attr) for anyone who
// wants it. Moved here verbatim from `AgentPanel.tsx` — this card is its only
// consumer — never reimplemented, per this story's own instruction.
const STEP_REF_RE = /^\$(\d+)\.[\w.-]+$/;

// Best-effort "what did this step create/change" noun, used only for the
// friendly phrase below — falls back to a de-slugged tool name so an
// unmapped capability still reads as English rather than snake_case.
const RESOURCE_NOUNS: Record<string, string> = {
  role_create: 'role',
  role_update: 'role update',
  member_invite: 'invite',
  member_invite_resend: 'invite',
  member_set_role: 'role assignment',
  team_create: 'team',
  org_create: 'organization',
  org_self_create: 'organization',
  org_convert: 'organization',
  org_flag_set: 'flag change',
  feature_set_state: 'flag change',
  plan_update: 'plan update',
  plan_assign: 'plan assignment',
  preset_commit_draft: 'preset',
  form_create_draft: 'draft',
  report_style_save: 'report style',
};
const resourceNounFor = (tool: string | undefined): string =>
  (tool && RESOURCE_NOUNS[tool]) || (tool ? tool.replace(/_/g, ' ') : 'result');

const describeStepRef = (raw: string, steps: AgentPlanCardStep[] | undefined): string => {
  const m = STEP_REF_RE.exec(raw);
  if (!m || !m[1]) return raw;
  const stepIndex = Number(m[1]);
  const noun = resourceNounFor(steps?.[stepIndex]?.tool);
  return `the ${noun} created in step ${stepIndex + 1}`;
};

/** Render a step/tool arg value: friendly phrase for a step reference (raw kept as a tooltip), plain text otherwise. */
const friendlyArgValue = (value: unknown, steps: AgentPlanCardStep[] | undefined): { text: string; title?: string } => {
  if (typeof value === 'string' && STEP_REF_RE.test(value)) {
    return { text: describeStepRef(value, steps), title: value };
  }
  // A ref can also be ONE element of an array arg (e.g. member_invite's
  // roleIds: ["$0.data.id"]) — resolveStepArgs (agents/src/gateway.js)
  // resolves refs anywhere in the arg tree, not just top-level strings.
  if (Array.isArray(value)) {
    const rawRefs = value.filter((v): v is string => typeof v === 'string' && STEP_REF_RE.test(v));
    const text = value
      .map((v) => (typeof v === 'string' && STEP_REF_RE.test(v) ? describeStepRef(v, steps) : typeof v === 'string' ? v : JSON.stringify(v)))
      .join(', ');
    return { text, ...(rawRefs.length > 0 ? { title: rawRefs.join(', ') } : {}) };
  }
  return { text: typeof value === 'string' ? value : JSON.stringify(value) };
};

const HIDDEN_ARG_KEYS = ['idempotency_key', 'draft_id'];

// The chip's tone/label come from `status` first, `outcome.isFailure`
// second — NOT from `isFailure` alone. `honestApprovalOutcome` (AgentPanel.tsx)
// returns `isFailure: false` for BOTH a genuinely approved outcome and a
// DECLINED one (`{ headline: 'Declined — nothing was changed.', isFailure:
// false }`) — that flag only ever distinguished "something went wrong" from
// "nothing went wrong," never "the user said yes" from "the user said no."
// The original inline markup never hit this: it only tinted the real
// headline text, it never rendered a second, separately-worded label. A
// badge that manufactures its own text has to get `declined` right
// explicitly, or a user who declines a plan sees a green "Approved" chip
// sitting directly above "Declined — nothing was changed."
const chipFor = (status: AgentPlanCardProps['status'], outcome: AgentPlanCardOutcome): { tone: 'success' | 'neutral' | 'destructive'; label: string } => {
  if (status === 'declined') return { tone: 'neutral', label: 'Declined' };
  if (outcome.isFailure) return { tone: 'destructive', label: 'Not completed' };
  return { tone: 'success', label: 'Approved' };
};

export function AgentPlanCard({
  tool,
  args,
  steps,
  status,
  outcome,
  onApprove,
  onDecline,
  className = '',
  'data-testid': testId,
}: AgentPlanCardProps): JSX.Element {
  const headline =
    steps && steps.length > 0
      ? `Approve this ${steps.length}-step plan?`
      : tool === 'preset_commit_draft'
        ? 'Save this form as a report preset?'
        : `Approve ${tool}?`;

  return (
    <div className={`rounded-xl border border-primary/40 bg-primary/5 p-3 text-xs ${className}`} data-testid={testId}>
      <p className="font-semibold text-foreground">{headline}</p>

      {steps && steps.length > 0 ? (
        // Batched plan (story 1D-2): one card, ordered sub-steps.
        <ol className="mt-1 space-y-1 text-muted-foreground">
          {/* Story 2-148 part 7 finding, fixed here: this optional summary line
              was a direct <p> child of <ol> — axe's `list` rule (serious,
              WCAG 1.3.1): only <li>/<script>/<template> may be a direct <ol>
              child. Wrapped in its own <li> (list-none — it isn't one of the
              numbered plan steps, so it gets no "N." marker, matching how
              every real <li> below already renders its own number as a span
              rather than relying on native ::marker). */}
          {typeof args.summary === 'string' && args.summary ? (
            <li className="list-none italic">{args.summary}</li>
          ) : null}
          {steps.map((step, si) => (
            <li key={si} className="flex gap-2">
              <span className="font-semibold text-foreground">{si + 1}.</span>
              <span>
                <span className="font-semibold">{step.tool}</span>
                {' — '}
                {Object.entries(step.args)
                  .filter(([k]) => !HIDDEN_ARG_KEYS.includes(k))
                  .map(([k, v], vi, arr) => {
                    const { text, title } = friendlyArgValue(v, steps);
                    return (
                      <span key={k}>
                        {k}: <span title={title}>{text}</span>
                        {vi < arr.length - 1 ? ', ' : ''}
                      </span>
                    );
                  })}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <div className="mt-1 space-y-0.5 text-muted-foreground">
          {Object.entries(args)
            .filter(([k]) => !HIDDEN_ARG_KEYS.includes(k))
            .map(([k, v]) => {
              const { text, title } = friendlyArgValue(v, steps);
              return (
                <p key={k}>
                  <span className="font-semibold">{k}:</span> <span title={title}>{text}</span>
                </p>
              );
            })}
        </div>
      )}

      {status === 'pending' ? (
        // Story 2-147r proof-of-concept: CardActions (align="start", the
        // default left-aligned layout `mt-2 flex gap-2` already used) —
        // same two Buttons/labels/order/roles as before, now
        // descriptor-driven.
        <div className="mt-2">
          <CardActions
            align="start"
            actions={
              [
                { id: 'approve', label: 'Approve', variant: 'primary', onClick: onApprove },
                { id: 'decline', label: 'Decline', variant: 'secondary', onClick: onDecline },
              ] as ActionDescriptor[]
            }
          />
        </div>
      ) : (
        outcome && (
          <div className="mt-2 flex items-start gap-2">
            <StatusChip {...chipFor(status, outcome)} value={status} />
            <div className="min-w-0">
              <p className={outcome.isFailure ? 'font-semibold text-destructive' : 'font-semibold text-muted-foreground'}>
                {outcome.headline}
              </p>
              {outcome.detail && (
                <details className="mt-1 text-muted-foreground">
                  <summary className="cursor-pointer select-none">Technical detail</summary>
                  <p className="mt-1 whitespace-pre-wrap">{outcome.detail}</p>
                </details>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
}
